import { createPopper } from '@popperjs/core'

import { components, isChildOf, numberToPxOrString, proxy, size } from '@/utils'

import render from './render'

export default {
  name: components.menu,

  mixins: [ proxy(), size ],

  props: {
    attach: null,

    placement: {
      type: String,
      default: 'bottom-start'
    },
    strategy: {
      type: String,
      default: 'fixed'
    },
    overflowY: {
      type: String,
      default: 'visible'
    },
    overflowX: {
      type: String,
      default: 'visible'
    },

    offsetSkidding: {
      type: Number,
      default: 0
    },
    offsetDistance: {
      type: Number,
      default: 0
    },

    closeOnContentClick: {
      type: Boolean,
      default: true
    },
    closeOnClick: {
      type: Boolean,
      default: true
    },

    width: {
      type: [ String, Number ],
      default: 'auto'
    },

    zIndex: {
      type: Number,
      default: 7
    },

    disabled: Boolean,
    rounded: Boolean,
    transparent: Boolean,

    sameWidth: Boolean,

    overflowBoundary: {
      type: [ String, Object ],
      default: 'viewport'
    }
  },

  data() {
    return {
      popperInstance: undefined,

      layoutResizeObserver: undefined,
      layoutUpdateFrameId: undefined,
      observedLayoutElements: [],

      resizeTimeoutId: undefined,
      windowResizeHandler: undefined
    }
  },

  computed: {
    $attach() {
      return this.attach || this.$refs.activator
    },

    modifiers() {
      let boundaryTarget = this.overflowBoundary
      if (!boundaryTarget) {
        if (this.$el && this.$el.ownerDocument) {
          boundaryTarget = this.$el.ownerDocument.documentElement
        }
      }

      const result = [
        {
          name: 'hideWhenMenuIsUnderToolbar',
          enabled: true,
          phase: 'main',
          requiresIfExists: [ 'offset' ],
          fn({ state }) {
            const toolbarElement = document.querySelector('nav#navigation.g-toolbar')
            const popperAttributes = state.attributes.popper
            if (toolbarElement) {
              if (toolbarElement.contains(state.elements.reference)) {
                popperAttributes['data-popper-reference-hidden'] = false
              } else {
                const referenceRectangle = state.rects.reference || {}
                const toolbarRectangle = toolbarElement.getBoundingClientRect()
                popperAttributes['data-popper-reference-hidden'] = referenceRectangle.y <= toolbarRectangle.bottom
              }
            } else {
              popperAttributes['data-popper-reference-hidden'] = false
            }
          }
        },
        {
          name: 'offset',
          options: {
            offset: ({ placement }) => {
              if (this.proxy && this.$attach && placement.includes('top') && !this.$refs.activator.clientHeight) {
                return [ this.offsetSkidding, this.$parent.$el.clientHeight ]
              } else {
                return [ this.offsetSkidding, this.offsetDistance ]
              }
            }
          }
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: boundaryTarget,
            rootBoundary: 'viewport',
            altBoundary: true,
            padding: 8,
            tether: true,
            altAxis: true
          }
        }
      ]

      if (this.sameWidth) {
        result.push({
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: [ 'computeStyles' ],
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`
          }
        })
      }

      return result
    },

    options() {
      return {
        placement: this.placement,
        modifiers: this.modifiers,
        strategy: this.strategy
      }
    },

    styles() {
      const styles = {
        'overflow-y': this.overflowY,
        'overflow-x': this.overflowX,
        'min-height': numberToPxOrString(this.minHeight),
        'max-height': numberToPxOrString(this.maxHeight),
        height: numberToPxOrString(this.height),
        'min-width': numberToPxOrString(this.minWidth),
        'max-width': numberToPxOrString(this.maxWidth),
        'z-index': this.zIndex
      }

      if (!this.sameWidth) {
        styles.width = numberToPxOrString(this.width)
      }

      return styles
    }
  },

  watch: {
    proxy(value) {
      if (value) {
        this.show()
      } else {
        this.hide()
      }
    }
  },

  mounted() {
    this.windowResizeHandler = () => {
      if (this.resizeTimeoutId !== undefined) {
        clearTimeout(this.resizeTimeoutId)
      }

      this.resizeTimeoutId = window.setTimeout(() => {
        this.update()
      }, 200)
    }

    window.addEventListener('resize', this.windowResizeHandler)

    if (document.body) {
      if (this.closeOnClick) {
        document.body.addEventListener('click', this.clickHandler)
      }
    }
  },

  beforeDestroy() {
    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler)
    }

    if (this.resizeTimeoutId !== undefined) {
      clearTimeout(this.resizeTimeoutId)
    }

    document.body.removeEventListener('click', this.clickHandler)

    this.hide()
  },

  methods: {
    create() {
      this.popperInstance = createPopper(
        this.$attach,
        this.$refs.content,
        this.options
      )

      this.$nextTick(() => {
        return this.update()
      })
    },
    update() {
      if (this.popperInstance) {
        this.popperInstance.update()
      }
    },
    scheduleUpdate() {
      if (this.layoutUpdateFrameId !== undefined) {
        window.cancelAnimationFrame(this.layoutUpdateFrameId)
      }

      this.layoutUpdateFrameId = window.requestAnimationFrame(() => {
        this.layoutUpdateFrameId = undefined
        this.update()
      })
    },
    destroy() {
      if (this.popperInstance) {
        this.popperInstance.destroy()
        this.popperInstance = undefined
      }
    },

    disconnectLayoutObserver() {
      if (this.layoutResizeObserver) {
        this.layoutResizeObserver.disconnect()
        this.layoutResizeObserver = undefined
      }

      this.observedLayoutElements = []

      if (this.layoutUpdateFrameId !== undefined) {
        window.cancelAnimationFrame(this.layoutUpdateFrameId)
        this.layoutUpdateFrameId = undefined
      }
    },

    show() {
      if (this.$refs.content) {
        if (document.body) {
          document.body.append(this.$refs.content)
        }

        this.create()
        this.observeLayoutChanges()
        this.$emit('show', true)
      }
    },

    hide() {
      this.disconnectLayoutObserver()
      this.destroy()
      this.$refs.content.remove()
      this.$emit('show', false)
    },

    checkForDisabled(target) {
      while (target) {
        if (
          target.className &&
          (typeof target.className === 'string' || Array.isArray(target.className)) &&
          (~target.className.indexOf('disabled') || target.hasAttribute('disabled'))
        ) {
          return true
        }
        target = target.parentElement
      }

      return false
    },

    contentClickHandler(event) {
      if (!!this.popperInstance && this.checkForDisabled(event.target)) {
        return
      }

      if (this.closeOnContentClick) {
        this.proxy = false
      }
    },

    clickHandler(event) {
      if (!this.closeOnContentClick && isChildOf(event.target, this.$refs.content)) {
        return
      }

      if (!this.disabled && !isChildOf(event.target, this.$refs.holder)) {
        this.proxy = false
      }
    },

    observeLayoutChanges() {
      this.disconnectLayoutObserver()
      if (typeof ResizeObserver === 'undefined') {
        return
      }

      this.layoutResizeObserver = new ResizeObserver(() => {
        this.scheduleUpdate()
      })

      const observedElements = new Set()
      const collectParentElements = element => {
        let currentElement = element
        while (currentElement) {
          observedElements.add(currentElement)
          if (currentElement === document.body) {
            break
          }
          currentElement = currentElement.parentElement
        }
      }

      collectParentElements(this.$attach)
      collectParentElements(this.$refs.holder)

      this.observedLayoutElements = Array.from(observedElements)
      this.observedLayoutElements.forEach(element => {
        this.layoutResizeObserver.observe(element)
      })
    }
  },

  render
}
